import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useAppState } from "../../state/appState";
import { useSession } from "../../state/session";
import { useToasts } from "../../state/toasts";
import { Empty } from "../../ui/components";
import { dateTime, money } from "../../ui/format";

/**
 * Устройства точки.
 *
 * Отвечает на один вопрос: всё ли работает. Без этого экрана «касса молчит»
 * узнают от гостя, который не смог оформить заказ, — а к тому моменту приём
 * уже остановлен, и смена ищет причину вслепую.
 */

const KIND_LABELS: Record<string, string> = {
  fiscal_sale: "Фискальный чек",
  fiscal_refund: "Чек возврата",
  fiscal_shift_open: "Открытие смены",
  fiscal_shift_close: "Закрытие смены"
};

const JOB_STATUS_LABELS: Record<string, string> = {
  queued: "В очереди",
  done: "Выполнено",
  failed: "Ошибка"
};

interface DeviceStatus {
  name: string;
  title: string;
  enabled: boolean;
  available: boolean;
  pending: number;
  lastSeenAt: string | null;
  reason: string;
}

interface FailedJob {
  id: string;
  kind: string;
  device: string;
  orderNumber?: string;
  attempts: number;
  error: string | null;
  updatedAt: string;
}

interface DevicesData {
  agent: { configured: boolean; lastSeenAt: string | null };
  devices: DeviceStatus[];
  debt: { id: string; number: number; total: number; paidAt: string | null }[];
  failed: FailedJob[];
  sync: { enabled: boolean; venueId: string; pending: number; oldestAt: string | null };
  venue: { watched: boolean; reachable: boolean; lastSeenAt: string | null } | null;
  fiscalState: {
    unsent: number;
    oldestAt: string | null;
    shiftOpen: boolean;
    paper: boolean;
    at: string | null;
  } | null;
}

export function DevicesPage() {
  const { snapshot } = useAppState();
  const { can } = useSession();
  const { run, notify } = useToasts();

  const [data, setData] = useState<DevicesData | null>(null);
  const [agentKey, setAgentKey] = useState<string | null>(null);

  const manageSettings = can("settings.manage");

  /*
   * Ключ запрашивается отдельно и только тем, кто может его перевыпустить:
   * в общий снимок состояния он не попадает — там его увидела бы вся смена.
   */
  useEffect(() => {
    if (!manageSettings) return;
    let cancelled = false;

    api
      .get<{ key: string }>("/api/admin/devices/agent")
      .then(result => {
        if (!cancelled) setAgentKey(result.key);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [manageSettings]);

  const load = useCallback(async () => {
    try {
      setData(await api.get<DevicesData>("/api/admin/devices"));
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось получить состояние устройств", "error");
    }
  }, [notify]);

  /*
   * Состояние перечитывается на каждое изменение снимка: любое движение заданий
   * и заказов и есть повод обновить экран. Отдельный опрос по таймеру был бы
   * вторым источником правды о том же самом.
   */
  useEffect(() => {
    void load();
  }, [load, snapshot?.deviceJobs.length, snapshot?.orders.length]);

  if (!data) return <Empty>Загрузка состояния устройств…</Empty>;

  const jobs = snapshot?.deviceJobs ?? [];

  async function reissueKey() {
    const confirmed = confirm(
      "Перевыпустить ключ агента? Агент, работающий со старым ключом, остановится — " +
        "на нём придётся прописать новый."
    );
    if (!confirmed) return;

    await run(async () => {
      const result = await api.once.post<{ key: string }>("/api/admin/devices/agent/reissue");
      setAgentKey(result.key);
    }, "Ключ агента перевыпущен");
  }

  async function retry(orderId: string, number: number) {
    const ok = await run(() => api.post(`/api/orders/${orderId}/fiscal-retry`), `Чек по заказу №${number} отправлен на кассу`);
    if (ok) await load();
  }

  async function fiscalShift(action: "open" | "close") {
    const title = action === "open" ? "Открытие смены отправлено на кассу" : "Закрытие смены отправлено на кассу";
    const ok = await run(() => api.post(`/api/admin/devices/fiscal-shift/${action}`), title);
    if (ok) await load();
  }

  const broken = data.devices.filter(device => device.enabled && !device.available);

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Точка</p>
          <h1>Устройства</h1>
        </div>
      </div>

      {broken.length > 0 ? (
        <section className="card device-alarm">
          <h2>Нужно вмешательство</h2>
          <p>{broken.map(device => `${device.title}: ${device.reason || "не отвечает"}`).join(" · ")}</p>
        </section>
      ) : null}

      <section className="card">
        <h2>Агент</h2>
        <p className="muted">
          {data.agent.configured
            ? "Ключ выдан — задания забирает программа на мини-ПК."
            : "Ключ не выдан: очередь разбирает симулятор, чеки не пробиваются."}
        </p>
        <p className="tiny muted">
          Последний отчёт: {data.agent.lastSeenAt ? dateTime(data.agent.lastSeenAt) : "ни разу"}
        </p>

        {manageSettings ? (
          <>
            <p className="muted">
              Сервер не может дотянуться до кассы: она висит на USB мини-ПК. Задания забирает агент —
              программа на той же машине. Пропишите этот ключ в её настройках.
            </p>

            <code className="tiny">{agentKey ?? "загрузка…"}</code>

            <div className="row-actions">
              <button type="button" className="btn btn-sm" onClick={() => void reissueKey()}>
                Перевыпустить ключ
              </button>
            </div>
          </>
        ) : null}
      </section>

      {data.fiscalState ? (
        <section className="card">
          <h2>Обмен с оператором фискальных данных</h2>

          {data.fiscalState.unsent === 0 ? (
            <p className="muted">
              Все документы переданы. Касса рассказала об этом {dateTime(data.fiscalState.at)}.
            </p>
          ) : (
            <>
              <p className="muted">
                Не передано документов: <b>{data.fiscalState.unsent}</b>
                {data.fiscalState.oldestAt ? <> · самый старый от {dateTime(data.fiscalState.oldestAt)}</> : null}
              </p>
              {/* Чек пробит и выдан гостю — это уже произошло. Но накопитель
                  хранит непереданное тридцать дней, после чего блокируется
                  и останавливает торговлю совсем. */}
              <p className="tiny muted">
                Чеки пробиты и выданы, но до оператора не дошли. Накопитель хранит их тридцать дней,
                дальше блокируется и останавливает торговлю. Проверьте связь и личный кабинет оператора.
              </p>
            </>
          )}

          <p className="tiny muted">
            Смена {data.fiscalState.shiftOpen ? "открыта" : "закрыта"} ·{" "}
            {data.fiscalState.paper ? "бумага есть" : "бумага кончилась"}
          </p>

          <div className="row-actions">
            <button type="button" className="btn btn-sm btn-primary" onClick={() => void fiscalShift("open")}>
              Открыть смену
            </button>
            <button type="button" className="btn btn-sm" onClick={() => void fiscalShift("close")}>
              Закрыть смену
            </button>
          </div>
        </section>
      ) : null}

      {data.venue?.watched ? (
        <section className="card">
          <h2>Связь с точкой</h2>

          {data.venue.reachable ? (
            <p className="muted">
              Точка на связи — заказы с лавочек принимаются. Последний раз приходила за работой{" "}
              {dateTime(data.venue.lastSeenAt)}.
            </p>
          ) : (
            <>
              <p className="muted">
                <b>Точка не на связи.</b>
                {data.venue.lastSeenAt
                  ? ` Последний раз приходила ${dateTime(data.venue.lastSeenAt)}.`
                  : " За заказами она ещё не приходила ни разу."}
              </p>
              {/* Гостю в этот момент отказывают, и владелец должен узнать об
                  этом раньше, чем гость: заказ, о котором на кухне не знают,
                  хуже честного отказа. */}
              <p className="tiny muted">
                Заказы с лавочек сейчас не принимаются: гость видит отказ и приглашение подойти
                к окну. Работе у окна это не мешает — там всё считает сама точка.
              </p>
            </>
          )}
        </section>
      ) : null}

      {data.sync.enabled ? (
        <section className="card">
          <h2>Выгрузка в облако</h2>

          {data.sync.pending === 0 ? (
            <p className="muted">Всё доехало. Точка «{data.sync.venueId}».</p>
          ) : (
            <>
              <p className="muted">
                Ждёт отправки: <b>{data.sync.pending}</b>. Самое старое — с{" "}
                {dateTime(data.sync.oldestAt)}.
              </p>
              {/* Копящаяся очередь — это не поломка, а отключённый интернет.
                  Работать точке это не мешает, и пугать смену не нужно. */}
              <p className="tiny muted">
                Пока интернета нет, сделанное копится здесь и уедет само, когда связь вернётся.
                На работу точки это не влияет.
              </p>
            </>
          )}
        </section>
      ) : null}

      <div className="metrics">
        {data.devices.map(device => (
          <div className="metric" key={device.name}>
            <b className="num">{device.pending}</b>
            <span>
              {device.title}
              <br />
              <small className="muted">
                {!device.enabled
                  ? "выключено"
                  : device.available
                    ? `в очереди · ${device.lastSeenAt ? dateTime(device.lastSeenAt) : "ни разу"}`
                    : device.reason || "не отвечает"}
              </small>
            </span>
          </div>
        ))}
      </div>

      {data.debt.length > 0 ? (
        <section className="card">
          <h2>Долг по чекам</h2>
          <p className="muted">
            Деньги приняты, чек не пробит. Пока долг есть, приём заказов остановлен: почините кассу
            и пробейте чеки заново.
          </p>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Заказ</th>
                  <th>Сумма</th>
                  <th>Оплачен</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.debt.map(order => (
                  <tr key={order.id} className="row-alert">
                    <td className="num">№{String(order.number).padStart(4, "0")}</td>
                    <td className="num">{money(order.total)}</td>
                    <td className="num">{dateTime(order.paidAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => void retry(order.id, order.number)}
                      >
                        Пробить заново
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="card">
        <h2>Невыполненные задания</h2>

        {data.failed.length === 0 ? (
          <Empty>Все задания выполнены.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Заказ</th>
                  <th>Задание</th>
                  <th>Устройство</th>
                  <th>Попытки</th>
                  <th>Ошибка</th>
                  <th>Когда</th>
                </tr>
              </thead>
              <tbody>
                {data.failed.map(job => (
                  <tr key={job.id} className="row-alert">
                    <td className="num">{job.orderNumber ? `№${job.orderNumber}` : "—"}</td>
                    <td>{KIND_LABELS[job.kind] ?? job.kind}</td>
                    <td className="tiny">{job.device}</td>
                    <td className="num">{job.attempts}</td>
                    <td className="tiny">{job.error ?? "—"}</td>
                    <td className="num">{dateTime(job.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Полная очередь, а не только сломанное: по ней видно, что агент вообще
          приходит за заданиями, а не только что он с чем-то не справился. */}
      <section className="card">
        <h2>Журнал заданий</h2>

        {jobs.length === 0 ? (
          <Empty>Заданий пока не было.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Заказ</th>
                  <th>Задание</th>
                  <th>Устройство</th>
                  <th>Статус</th>
                  <th>Попытки</th>
                  <th>Создано</th>
                  <th>Выполнено</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.id} className={job.status === "failed" ? "row-alert" : ""}>
                    <td className="num">{job.orderNumber ? `№${job.orderNumber}` : "—"}</td>
                    <td>{KIND_LABELS[job.kind] ?? job.kind}</td>
                    <td className="tiny">{job.device}</td>
                    <td>
                      <span className="chip">{JOB_STATUS_LABELS[job.status] ?? job.status}</span>
                      {job.error ? (
                        <>
                          <br />
                          <small className="muted">{job.error}</small>
                        </>
                      ) : null}
                    </td>
                    <td className="num">{job.attempts}</td>
                    <td className="num">{dateTime(job.createdAt)}</td>
                    <td className="num">{dateTime(job.doneAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
